import React, { useCallback, useState } from 'react'
import { Redirect, useHistory } from 'react-router-dom'
import { useMutation } from 'react-query'
import { API_ERROR_CODES } from '@customTypes/Generic'
import { AppointmentConfirmationScreen } from '@screens/AppointmentConfirmation/AppointmentConfirmation'
import { setAfterBookingData, useConfig, useConfigDispatch } from '@context/ConfigurationContext'
import { useGapFillerQueryParams } from '@hooks/useQueryParams'
import useComponentDidMount from '@hooks/useComponentDidMount'
import routes from 'src/config/routes'
import { prepareAppointmentRescheduleSuccessUrl } from 'src/utils/prepareAppointmentRescheduleSuccessUrl'
import { postGapFillerBooking } from 'src/services/api/booking'
import {
  seenAppointmentConfirmation,
  analyticsEventGapFillerFailed,
  analyticsEventGapFillerSucceed,
  analyticsEventGapFillerPastDate,
  analyticsEventRescheduleMissingParam,
} from 'src/services/analytics'
import captureExceptionWithResponse from 'src/services/sentry'
import { GapFillerQueryParams } from '@customTypes/GapFiller'
import { GapFillerDetails } from '@components/GapFiller/AppointmentConfirmationDetails'
import { useAppointmentStart } from '@hooks/useAppointmentStart'
import { useHasUserSelectedSlot } from '@hooks/useHasUserSelectedSlot'
import { useRecommendedPractitioner } from '@hooks/useRecommendedPractitioner'
import { getErrorTranslationKey } from 'src/helpers/getApiErrorTranslationKey'
import { useCreationEvent } from '@hooks/useCreationEvent'
import { getAfterBookingStateFromBookingResponse } from 'src/utils/getAfterBookingStateFromBookingResponse'

const getAnalyticsPayload = (queryParams?: GapFillerQueryParams) => {
  if (!queryParams) {
    return undefined
  }

  return {
    start: queryParams.start,
    appointmentBundleReferenceId: queryParams.appointmentBundleReferenceId,
  }
}

const AppointmentConfirmationGapFiller: React.FC = () => {
  const history = useHistory()
  const { successUrl, sourceWebsiteType } = useConfig()
  const [shouldRedirect, setShouldRedirect] = useState(false)
  const [error, setError] = useState('')
  const { data: practitioner } = useRecommendedPractitioner()
  const configDispatch = useConfigDispatch()
  const creationEvent = useCreationEvent()

  const {
    hasAllRequiredParams,
    queryParams,
    state: { hasStartInUrl, isStartDateValid },
  } = useGapFillerQueryParams()

  const appointmentStart = useAppointmentStart(queryParams?.start)
  const hasUserSelectedSlot = useHasUserSelectedSlot()
  const canGoBack = Boolean(queryParams?.treatmentBundleReferenceId || queryParams?.treatmentReferenceId)

  const { mutate: confirmAppointment, isLoading: isProcessing } = useMutation(postGapFillerBooking, {
    onSuccess: (response) => {
      configDispatch(setAfterBookingData(getAfterBookingStateFromBookingResponse(response.data)))
      analyticsEventGapFillerSucceed(getAnalyticsPayload(queryParams))
      if (successUrl) {
        window.location.href = prepareAppointmentRescheduleSuccessUrl(successUrl, appointmentStart)
        return
      }

      history.push(routes.success)
    },
    onError: (err: Record<string, any>) => {
      analyticsEventGapFillerFailed(getAnalyticsPayload(queryParams))
      if (
        err.response?.data?.errorCode === API_ERROR_CODES.BOOKING_SLOT_GONE ||
        err.response?.data?.errorCode === API_ERROR_CODES.INVALID_REQUEST_PARAM_VALUE
      ) {
        setError('api_errors.gap_filler_booking_slot_gone')
      } else {
        setError(getErrorTranslationKey(err))
      }
    },
  })

  const processBooking = useCallback(
    (token?: string) => {
      if (queryParams?.appointmentBundleReferenceId && appointmentStart) {
        confirmAppointment({
          appointmentBundleReferenceId: queryParams.appointmentBundleReferenceId,
          start: new Date(appointmentStart).toISOString(),
          trackingParams: queryParams,
          practitionerId: practitioner?.referenceId,
          creationSource: sourceWebsiteType,
          creationEvent,
        })
      }
    },
    [confirmAppointment, queryParams, appointmentStart, practitioner?.referenceId, sourceWebsiteType, creationEvent]
  )

  useComponentDidMount(() => {
    seenAppointmentConfirmation()

    if (!hasAllRequiredParams) {
      captureExceptionWithResponse('Gap Filler: Missing required query params', { queryParams })
      analyticsEventRescheduleMissingParam(getAnalyticsPayload(queryParams))
      return
    }

    if (hasStartInUrl && !isStartDateValid && !hasUserSelectedSlot) {
      analyticsEventGapFillerPastDate(getAnalyticsPayload(queryParams))
      setShouldRedirect(true)
    }

    if (!appointmentStart) {
      setShouldRedirect(true)
    }
  })

  const handleNextClicked = () => {
    return processBooking()
  }

  const handleBackClicked = () => {
    history.push(routes.appointment)
  }

  if (shouldRedirect && canGoBack) {
    return <Redirect to={routes.appointment} />
  }

  return (
    <>
      <AppointmentConfirmationScreen
        onNextClicked={handleNextClicked}
        onBackClicked={canGoBack ? handleBackClicked : undefined}
        isProcessing={isProcessing}
        error={error}
        showGenericError={!hasAllRequiredParams || (!hasStartInUrl && !canGoBack)}
        canGoNext={!error}
        bookButtonToken="book_appointment_gap_filler"
      >
        {appointmentStart ? (
          <GapFillerDetails
            selectedSlot={new Date(appointmentStart)}
            originalSlot={queryParams?.originalDateTime ? new Date(queryParams.originalDateTime) : undefined}
          />
        ) : null}
      </AppointmentConfirmationScreen>
    </>
  )
}

export default AppointmentConfirmationGapFiller
